// Reading and writing vehicle identifiers in both lookup modes. UK customers key vehicles by
// registration; VIN-mode customers key them by the 17-character chassis number and may type a
// plate alongside it. Every component that asks "which identifier, what label, is it valid"
// goes through here so the two modes cannot drift.
import { DateTime } from "luxon";

import { IVehicle, IVehicleLookupConfig, VehicleIdentifierType } from "../models/Vehicle";
import { normaliseVrm } from "./vehicleScan";

export const VIN_LENGTH = 17;

// VINs never contain I, O or Q. No check digit: it is only reliable for North American VINs.
const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

export const DEFAULT_LOOKUP_CONFIG: IVehicleLookupConfig = {
  identifierType: "Registration",
  asynchronous: false,
  registrationOptional: false,
  supportsPackages: true,
  supportsEnginesWorldwide: true,
  supportsCatalyticData: true,
};

/** Uppercase, no whitespace. Drops the Code 39 import character a keyboard-wedge scanner types. */
export const normaliseVin = (value?: string | null): string => {
  let vin = (value ?? "").replace(/\s+/g, "").toUpperCase();

  if (vin.length === VIN_LENGTH + 1 && vin.startsWith("I") && VIN_PATTERN.test(vin.slice(1))) {
    vin = vin.slice(1);
  }

  return vin;
};

export const isPlausibleVin = (value?: string | null): boolean => VIN_PATTERN.test(normaliseVin(value));

/** A plausible 17-character VIN is never a registration mark, so the shape decides. */
export const classifyIdentifier = (value?: string | null): VehicleIdentifierType =>
  isPlausibleVin(value) ? "Vin" : "Registration";

export const normaliseIdentifier = (value: string | null | undefined, type: VehicleIdentifierType): string =>
  type === "Vin" ? normaliseVin(value) : normaliseVrm(value);

/** Whether a typed value is complete enough to send to a lookup. */
export const isValidIdentifier = (value: string | null | undefined, type: VehicleIdentifierType): boolean =>
  type === "Vin" ? isPlausibleVin(value) : normaliseVrm(value).length > 0;

export const identifierField = (type: VehicleIdentifierType): "vrm" | "vin" => (type === "Vin" ? "vin" : "vrm");

export const identifierLabel = (type: VehicleIdentifierType): string => (type === "Vin" ? "VIN" : "VRM");

export const identifierPlaceholder = (type: VehicleIdentifierType): string =>
  type === "Vin" ? "e.g. WVWZZZ1KZAW123456" : "e.g. KT68XVX";

export const identifierMaxLength = (type: VehicleIdentifierType): number | undefined =>
  type === "Vin" ? VIN_LENGTH : undefined;

export const identifierValidationMessage = (type: VehicleIdentifierType): string =>
  type === "Vin" ? "Enter a valid 17-character VIN" : "Enter a registration";

type VehicleIdentity = Pick<IVehicle, "vrm"> & Partial<Pick<IVehicle, "combinedVIN" | "vinVerified" | "combinedVINLast8">>;

/** What to send to a lookup or refresh for a vehicle we hold. */
export const vehicleIdentifierFor = (vehicle: VehicleIdentity, type: VehicleIdentifierType): string =>
  type === "Vin" ? vehicle.vinVerified || vehicle.combinedVIN || "" : vehicle.vrm || "";

/** The last eight characters, the part of a VIN people read out and print on labels. */
export const vinLast8 = (vin?: string | null): string | null => {
  const normalised = normaliseVin(vin);
  if (!normalised) return null;

  return normalised.length > 8 ? normalised.slice(-8) : normalised;
};

/** The plate when there is one, else the VIN tail; null when the vehicle has neither. */
export const vehicleDisplayIdentifier = (
  vehicle: Partial<Pick<IVehicle, "vrm" | "combinedVIN" | "vinVerified" | "combinedVINLast8">> | null | undefined
): string | null => {
  if (!vehicle) return null;

  const vrm = vehicle.vrm?.trim();
  if (vrm) return vrm;

  return vinLast8(vehicle.vinVerified || vehicle.combinedVIN) || vehicle.combinedVINLast8?.trim() || null;
};

// The heuristic the synchronous create pages have always used for "did this vehicle exist before I
// looked it up": a row created more than a minute ago. Asynchronous lookups get `existed` from the
// API instead.
export const vehicleExistedBeforeLookup = (
  vehicle: Pick<IVehicle, "created"> | null | undefined,
  now: DateTime = DateTime.now()
): boolean => {
  if (!vehicle?.created) return false;

  const created = DateTime.fromISO(vehicle.created);

  return created.isValid && created < now.minus({ minutes: 1 });
};

const identifierTypeFrom = (raw: unknown): VehicleIdentifierType | null => {
  if (raw === "Vin" || raw === "vin" || raw === 1) return "Vin";
  if (raw === "Registration" || raw === "registration" || raw === 0) return "Registration";

  return null;
};

/** Parses the config endpoint's payload, tolerating enum numbers; anything unusable is the default. */
export const parseLookupConfig = (raw: unknown): IVehicleLookupConfig => {
  if (!raw || typeof raw !== "object") return DEFAULT_LOOKUP_CONFIG;

  const value = raw as Record<string, unknown>;
  const identifierType = identifierTypeFrom(value.identifierType);
  if (!identifierType) return DEFAULT_LOOKUP_CONFIG;

  const flag = (key: string, fallback: boolean) => (typeof value[key] === "boolean" ? (value[key] as boolean) : fallback);

  return {
    identifierType,
    asynchronous: flag("asynchronous", false),
    registrationOptional: flag("registrationOptional", identifierType === "Vin"),
    supportsPackages: flag("supportsPackages", identifierType === "Registration"),
    supportsEnginesWorldwide: flag("supportsEnginesWorldwide", identifierType === "Registration"),
    supportsCatalyticData: flag("supportsCatalyticData", identifierType === "Registration"),
  };
};
